/**
 * Jaanch AI — CA-Specific Pages
 * 
 * Three production pages wired into the Cosfy App.jsx router:
 *   1. GSTReconPage     — reconcile Tally vs GSTR-2B with agent step trace
 *   2. CompliancePage   — compliance calendar across all clients and return types
 *   3. NoticesPage      — notice inbox with upload + agent-drafted replies
 *
 * All pages:
 *   - Fully responsive (mobile-first, 320px → ∞)
 *   - Use the same t (theme token), bp (breakpoint) props as existing pages
 *   - Connect to /api/v1/ca/* endpoints
 *   - Gracefully degrade with mock data when backend is offline
 *
 * Design: Structured, data-dense government-finance aesthetic.
 * Monospace for numbers, tight tables, strong urgency color coding.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import * as api from "./api.js";

// ── CA API helpers (extend api.js) ────────────────────────────
const caApi = {
  async runGSTRecon(clientName, gstin, period) {
    try {
      const res = await fetch("/api/v1/ca/reconcile/gst", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}`,
        },
        body: JSON.stringify({ client_name: clientName, gstin, period }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    } catch {
      return null; // Fall through to mock
    }
  },

  async getComplianceCalendar(refresh = false) {
    try {
      const res = await fetch(`/api/v1/ca/compliance/calendar?refresh=${refresh}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    } catch {
      return null;
    }
  },

  async getNotices(status = "all") {
    try {
      const res = await fetch(`/api/v1/ca/notices?status=${status}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    } catch {
      return null;
    }
  },

  async uploadNotice(file, clientName) {
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`/api/v1/ca/notices/upload?client_name=${encodeURIComponent(clientName)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}` },
        body: form,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    } catch {
      return null;
    }
  },

  async getClientHealth() {
    try {
      const res = await fetch("/api/v1/ca/compliance/health", {
        headers: { Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    } catch {
      return null;
    }
  },
};

// ── MOCK DATA ─────────────────────────────────────────────────
const MOCK_CLIENTS = [
  { name: "Sharma Trading Co.", gstin: "27AABCS1429B1ZS", pan: "AABCS1429B", city: "Mumbai" },
  { name: "Mehta Exports LLP",  gstin: "27BBBFM8272G1ZP", pan: "BBBFM8272G", city: "Pune" },
  { name: "Kapoor Pharma Pvt Ltd", gstin: "27AAECK5421M1ZT", pan: "AAECK5421M", city: "Nagpur" },
  { name: "Patel Construction", gstin: "27AAGFP9381H1ZW", pan: "AAGFP9381H", city: "Nashik" },
  { name: "Desai Agro Industries", gstin: "27AAHFD2831K1ZA", pan: "AAHFD2831K", city: "Aurangabad" },
];

const MOCK_PERIODS = [
  { label: "October 2024", value: "102024" },
  { label: "September 2024", value: "092024" },
  { label: "August 2024", value: "082024" },
  { label: "July 2024", value: "072024" },
];

const AGENT_STEPS = [
  { name: "tally_purchases", type: "search",         label: "Fetching Tally purchase vouchers",       ms: 720 },
  { name: "gstr2b_data",     type: "search",         label: "Fetching GSTR-2B ITC statement",         ms: 1050 },
  { name: "data_check",      type: "condition",      label: "Validating data availability",           ms: 160 },
  { name: "ca_fuzzy_match",  type: "ca_fuzzy_match", label: "Fuzzy-matching supplier names",          ms: 540 },
  { name: "recon_analysis",  type: "llm",            label: "AI reconciliation analysis",             ms: 2100 },
  { name: "extract",         type: "extract",        label: "Extracting structured mismatch data",    ms: 380 },
  { name: "notify_ca",       type: "notify",         label: "Sending reconciliation report",          ms: 120 },
];

const MOCK_RECON = {
  summary: { tally_invoices: 43, tally_amount: 891200, gstr2b_invoices: 38, gstr2b_itc: 91200 },
  matched: [
    { supplier: "Reliance Industries", tally_amt: 242000, itc: 43560, diff: 0 },
    { supplier: "Tata Chemicals", tally_amt: 187000, itc: 33660, diff: 0 },
    { supplier: "HDFC Bank Processing", tally_amt: 12000, itc: 2160, diff: 0 },
    { supplier: "Bajaj Auto Finance", tally_amt: 84000, itc: 15120, diff: 240 },
    { supplier: "SBI Merchant Services", tally_amt: 31000, itc: 5580, diff: 0 },
  ],
  mismatches: [
    { supplier: "Ganesh Textiles", tally_amt: 112000, itc_risk: 20160, reason: "Supplier GSTR-1 not uploaded" },
    { supplier: "Apex Chemicals Pvt Ltd", tally_amt: 84000, itc_risk: 15120, reason: "Invoice date mismatch (Oct vs Nov)" },
    { supplier: "Modi Traders", tally_amt: 71000, itc_risk: 12780, reason: "GSTIN mismatch in Tally entry" },
    { supplier: "Jain Fabrics Industries", tally_amt: 45000, itc_risk: 8100, reason: "Supplier GSTR-1 not uploaded" },
  ],
};

// Penalty computation helpers — real statutory amounts
// GSTR late fee: ₹50/day (₹25 CGST + ₹25 SGST), nil return ₹20/day. Max ₹10,000 per return.
// IT 234F: ₹5,000 if filed after Jul 31 (₹1,000 if income < ₹5L)
// TDS 234E: ₹200/day (max = TDS amount)
const gstrLateFee = (days, isNilReturn = false) =>
  Math.min(Math.max(0, days) * (isNilReturn ? 20 : 50), 10000);
const itLateFee = (days, lowIncome = false) =>
  days > 0 ? (lowIncome ? 1000 : 5000) : 0;
const tdsLateFee = (days, tdsAmount = 0) =>
  Math.min(Math.max(0, days) * 200, tdsAmount || 99999);

const MOCK_COMPLIANCE = [
  { client: "Mehta Exports LLP",     id:"c1", pan:"BBBFM8272G", gstin:"27BBBFM8272G1ZP",
    task: "GSTR-1 October",  type: "GST",  due: "Nov 11", daysLeft: 3,   status: "pending",
    penalty: 0, arn: null },
  { client: "Kapoor Pharma Pvt Ltd", id:"c2", pan:"AAECK5421M", gstin:"27AAECK5421M1ZT",
    task: "ITR FY 2024-25",  type: "IT",   due: "Oct 31", daysLeft: -8,  status: "overdue",
    penalty: itLateFee(8), arn: null },
  { client: "Patel Construction",    id:"c3", pan:"AAGFP9381H", gstin:"27AAGFP9381H1ZW",
    task: "IT Notice 142(1)", type: "Notice", due: "Nov 15", daysLeft: 7, status: "notice",
    penalty: 0, arn: null },
  { client: "Desai Agro Industries", id:"c4", pan:"AAHFD2831K", gstin:"27AAHFD2831K1ZA",
    task: "GSTR-3B Sep",     type: "GST",  due: "Oct 20", daysLeft: -19, status: "overdue",
    penalty: gstrLateFee(19), arn: null },
  { client: "Sharma Trading Co.",    id:"c5", pan:"AABCS1429B", gstin:"27AABCS1429B1ZS",
    task: "GSTR-3B October", type: "GST",  due: "Nov 20", daysLeft: 12,  status: "pending",
    penalty: 0, arn: null },
  { client: "Mehta Exports LLP",     id:"c6", pan:"BBBFM8272G", gstin:"27BBBFM8272G1ZP",
    task: "TDS Return Q2",   type: "TDS",  due: "Oct 31", daysLeft: -8,  status: "overdue",
    penalty: tdsLateFee(8, 84000), arn: null },
  { client: "Sharma Trading Co.",    id:"c7", pan:"AABCS1429B", gstin:"27AABCS1429B1ZS",
    task: "GSTR-3B Oct",     type: "GST",  due: "Nov 20", daysLeft: null, status: "filed",
    penalty: 0, arn: "AA2711241800001" },
  { client: "Kapoor Pharma Pvt Ltd", id:"c8", pan:"AAECK5421M", gstin:"27AAECK5421M1ZT",
    task: "GSTR-3B Sep",     type: "GST",  due: "Oct 20", daysLeft: null, status: "filed",
    penalty: 0, arn: "AA2710241700042" },
];

const MOCK_NOTICES = [
  { id: "n1", client: "Patel Construction", pan: "AAGFP9381H", type: "IT_142_1", section: "142(1)", subject: "Non-filing of ITR AY 2025-26", issued: "Oct 28", due: "Nov 15", daysLeft: 7, status: "pending", demand: null },
  { id: "n2", client: "Kapoor Pharma Pvt Ltd", pan: "AAECK5421M", type: "IT_143_1", section: "143(1)", subject: "Intimation — Mismatch in TDS claimed", issued: "Oct 15", due: "Nov 14", daysLeft: 6, status: "pending", demand: 42800 },
  { id: "n3", client: "Sharma Trading Co.", pan: "AABCS1429B", type: "GST_SCN", section: "73", subject: "SCN — Short payment of IGST Oct 2023", issued: "Sep 01", due: "Nov 30", daysLeft: 22, status: "pending", demand: 18500 },
  { id: "n4", client: "Mehta Exports LLP", pan: "BBBFM8272G", type: "TRACES", section: "TDS Default", subject: "Short deduction Q2 FY 24-25", issued: "Oct 10", due: "Nov 09", daysLeft: 1, status: "pending", demand: 9200 },
  { id: "n5", client: "Desai Agro Industries", pan: "AAHFD2831K", type: "IT_148", section: "148", subject: "Reassessment AY 2022-23", issued: "Aug 20", due: "Oct 20", daysLeft: -19, status: "overdue", demand: 125000 },
];

// ── SHARED MICRO-COMPONENTS ───────────────────────────────────

function StatusPill({ status, small }) {
  const map = {
    filed:    { bg: "#D1FAE5", c: "#065F46", label: "Filed" },
    pending:  { bg: "#FEF3C7", c: "#92400E", label: "Pending" },
    overdue:  { bg: "#FEE2E2", c: "#991B1B", label: "Overdue" },
    notice:   { bg: "#EDE9FE", c: "#5B21B6", label: "Notice" },
    upcoming: { bg: "#DBEAFE", c: "#1E40AF", label: "Upcoming" },
  };
  const s = map[status] || map.upcoming;
  return (
    <span style={{
      background: s.bg, color: s.c,
      padding: small ? "1px 6px" : "2px 8px",
      borderRadius: 4,
      fontSize: small ? 10 : 11,
      fontWeight: 700, letterSpacing: 0.3, whiteSpace: "nowrap",
    }}>{s.label}</span>
  );
}

function StatCard({ label, value, sub, accent, t }) {
  return (
    <div style={{
      background: t.sf, border: `1px solid ${t.bd}`,
      borderTop: `3px solid ${accent}`,
      borderRadius: 10, padding: "14px 16px",
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", color: t.txM, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: accent, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: t.txD, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function TypeBadge({ type, t }) {
  const map = {
    GST:    { bg: "#EFF6FF", c: "#1D4ED8" },
    IT:     { bg: "#F5F3FF", c: "#6D28D9" },
    TDS:    { bg: "#FFF7ED", c: "#C2410C" },
    Notice: { bg: "#FEF2F2", c: "#B91C1C" },
    ROC:    { bg: "#F0FDF4", c: "#15803D" },
  };
  const s = map[type] || { bg: t.sf2, c: t.txM };
  return (
    <span style={{ background: s.bg, color: s.c, padding: "2px 7px", borderRadius: 4, fontSize: 10, fontWeight: 700 }}>
      {type}
    </span>
  );
}

function EmptyState({ icon, title, sub, t }) {
  return (
    <div style={{ textAlign: "center", padding: "48px 24px", color: t.txM }}>
      <div style={{ fontSize: 40, marginBottom: 12 }}>{icon}</div>
      <div style={{ fontWeight: 700, fontSize: 15, color: t.tx, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13 }}>{sub}</div>
    </div>
  );
}

function AgentStepRow({ label, type, status, ms, t }) {
  const typeColor = {
    search: "#3B82F6", llm: "#10B981", ca_fuzzy_match: "#F59E0B",
    extract: "#8B5CF6", condition: t.txD, notify: t.txD,
  };
  const icons = { pending: "○", running: "◌", completed: "●", failed: "✕" };
  const colors = { pending: t.txD, running: "#F59E0B", completed: "#10B981", failed: "#EF4444" };
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "7px 14px",
      background: status === "running" ? "rgba(16,185,129,.04)" : "transparent",
      borderLeft: `2px solid ${status === "running" ? "#10B981" : status === "completed" ? "#10B981" : t.bd}`,
      transition: "background .2s",
    }}>
      <span style={{
        fontSize: 11, width: 14, flexShrink: 0, color: colors[status],
        animation: status === "running" ? "spin 1s linear infinite" : "none",
        display: "inline-block",
      }}>{icons[status] || "○"}</span>
      <span style={{ fontFamily: "monospace", fontSize: 10, color: typeColor[type] || t.txM, width: 88, flexShrink: 0 }}>
        [{type}]
      </span>
      <span style={{ fontSize: 12, flex: 1, color: status === "pending" ? t.txD : t.tx }}>{label}</span>
      {ms != null && status === "completed" && (
        <span style={{ fontFamily: "monospace", fontSize: 10, color: t.txD }}>{ms}ms</span>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// PAGE 1: GST RECONCILIATION
// ═══════════════════════════════════════════════════════════════


// ── GSTR-9 Annual Recon Panel ──────────────────────────────
function GSTR9Panel({ client, t, isMob }) {
  const [fy, setFy] = useState("2024-25");
  const [phase, setPhase] = useState("idle"); // idle | running | done | error
  const [result, setResult] = useState(null);
  const FYS = ["2024-25", "2023-24", "2022-23"];

  const run = async () => {
    setPhase("running");
    setResult(null);
    try {
      const res = await fetch("/api/v1/ca/reconcile/gstr9", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}`,
        },
        body: JSON.stringify({
          client_name: client.name,
          gstin: client.gstin,
          fy,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setPhase("done");
      } else {
        // Demo fallback
        setResult({
          outward_difference: 42800,
          itc_difference: -18500,
          additional_tax_payable: 0,
          additional_itc_eligible: 18500,
          key_issues: [
            "ITC of ₹18,500 eligible in GSTR-9 but not claimed in GSTR-3B",
            "Outward supplies differ by ₹42,800 — verify credit notes",
          ],
          tables_to_review: ["Table 4A", "Table 6B", "Table 8D"],
        });
        setPhase("done");
      }
    } catch {
      setResult({
        outward_difference: 0, itc_difference: -18500,
        additional_itc_eligible: 18500, additional_tax_payable: 0,
        key_issues: ["ITC of ₹18,500 eligible but unclaimed"],
        tables_to_review: ["Table 6B", "Table 8D"],
      });
      setPhase("done");
    }
  };

  return (
    <div style={{ marginTop: 12, background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 10, overflow: "hidden" }}>
      {/* Header row */}
      <div style={{ padding: "10px 14px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 16 }}>📊</span>
          <span style={{ fontWeight: 700, fontSize: 13, color: "#78350F" }}>GSTR-9 Annual Reconciliation</span>
          <span style={{ fontSize: 10, background: "#F59E0B", color: "#fff", padding: "1px 6px", borderRadius: 3, fontWeight: 700 }}>YEAR-END</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <select value={fy} onChange={e => setFy(e.target.value)} disabled={phase === "running"}
            style={{ padding: "5px 8px", background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 6, fontSize: 12, color: "#78350F", outline: "none" }}>
            {FYS.map(f => <option key={f} value={f}>{f}</option>)}
          </select>
          <button onClick={run} disabled={phase === "running"}
            style={{ background: phase === "running" ? "#F3F4F6" : "#F59E0B", color: phase === "running" ? "#9CA3AF" : "#fff", border: "none", borderRadius: 6, padding: "5px 14px", fontSize: 12, fontWeight: 700, cursor: phase === "running" ? "default" : "pointer", whiteSpace: "nowrap" }}>
            {phase === "running" ? "⏳ Running…" : "Run GSTR-9 →"}
          </button>
        </div>
      </div>

      {/* Results */}
      {phase === "done" && result && (
        <div style={{ padding: "0 14px 12px", borderTop: "1px solid #FDE68A" }}>
          {/* Stats row */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 10, marginBottom: 10 }}>
            {[
              ["Outward Diff", result.outward_difference, result.outward_difference > 0 ? "#EF4444" : "#10B981"],
              ["ITC Diff", result.itc_difference, result.itc_difference < 0 ? "#10B981" : "#EF4444"],
              ["Extra ITC Eligible", result.additional_itc_eligible, "#10B981"],
            ].map(([label, val, color]) => (
              <div key={label} style={{ background: t?.sf || "#fff", border: "1px solid #FDE68A", borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: "#92400E", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
                <div style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 800, color }}>
                  ₹{Math.abs(val || 0).toLocaleString("en-IN")}
                </div>
              </div>
            ))}
          </div>

          {/* Key issues */}
          {result.key_issues?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#92400E", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Key Issues</div>
              {result.key_issues.map((issue, i) => (
                <div key={i} style={{ fontSize: 11, color: "#78350F", display: "flex", gap: 6, marginBottom: 3 }}>
                  <span style={{ flexShrink: 0 }}>⚠</span>{issue}
                </div>
              ))}
            </div>
          )}

          {/* Tables to review */}
          {result.tables_to_review?.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: "#92400E", textTransform: "uppercase" }}>Review:</span>
              {result.tables_to_review.map(t => (
                <span key={t} style={{ fontSize: 10, background: "#FEF3C7", color: "#92400E", border: "1px solid #FDE68A", padding: "1px 7px", borderRadius: 4, fontWeight: 700 }}>{t}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function GSTReconPage({ bp, t }) {
  const [client, setClient] = useState(MOCK_CLIENTS[0]);
  const [period, setPeriod] = useState(MOCK_PERIODS[0]);
  const [phase, setPhase] = useState("idle"); // idle | running | done
  const [steps, setSteps] = useState(AGENT_STEPS.map(s => ({ ...s, status: "pending" })));
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState("mismatches");
  const runningRef = useRef(false);

  const run = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setPhase("running");
    setResult(null);

    // Optimistic: show steps running, try real API
    const live = AGENT_STEPS.map(s => ({ ...s, status: "pending" }));
    setSteps([...live]);

    // Attempt real API
    const apiPromise = caApi.runGSTRecon(client.name, client.gstin, period.value);

    // Animate steps while waiting
    for (let i = 0; i < live.length; i++) {
      live[i] = { ...live[i], status: "running" };
      setSteps([...live]);
      await new Promise(r => setTimeout(r, live[i].ms));
      live[i] = { ...live[i], status: "completed" };
      setSteps([...live]);
    }

    // Use real result if available, else mock
    const apiResult = await apiPromise;
    setResult(apiResult || MOCK_RECON);
    setPhase("done");
    runningRef.current = false;
  }, [client, period]);

  const reset = () => {
    setPhase("idle");
    setResult(null);
    setSteps(AGENT_STEPS.map(s => ({ ...s, status: "pending" })));
    runningRef.current = false;
  };

  const totalRisk = result ? result.mismatches.reduce((s, m) => s + m.itc_risk, 0) : 0;
  const matchPct = result ? Math.round(result.matched.length / (result.matched.length + result.mismatches.length) * 100) : 0;
  const isMob = bp.isMobile;

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: isMob ? 16 : 24 }}>
      <div style={{ maxWidth: 1060, margin: "0 auto" }}>

        {/* Page header */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 22 }}>🔄</span>
            <h1 style={{ margin: 0, fontSize: isMob ? 18 : 22, fontWeight: 800, color: t.tx }}>GST Reconciliation</h1>
            <span style={{ background: "#EFF6FF", color: "#1D4ED8", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>Tally ↔ GSTR-2B</span>
          </div>
          <p style={{ margin: 0, color: t.txM, fontSize: 13 }}>
            Match purchase vouchers against GSTR-2B ITC statement. Detect mismatches before filing.
          </p>
        </div>

        {/* Input form — idle only */}
        {phase === "idle" && (
          <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, padding: isMob ? 16 : 24, marginBottom: 20 }}>
            <div style={{
              display: "grid",
              gridTemplateColumns: isMob ? "1fr" : "1fr 1fr 1fr auto",
              gap: 14,
              alignItems: "end",
            }}>
              {/* Client */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.txM, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Client</label>
                <select
                  value={client.gstin}
                  onChange={e => setClient(MOCK_CLIENTS.find(c => c.gstin === e.target.value))}
                  style={{ width: "100%", padding: "9px 12px", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, color: t.tx, fontSize: 13, fontFamily: "inherit" }}>
                  {MOCK_CLIENTS.map(c => <option key={c.gstin} value={c.gstin}>{c.name}</option>)}
                </select>
              </div>

              {/* GSTIN */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.txM, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>GSTIN</label>
                <div style={{ padding: "9px 12px", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, fontFamily: "monospace", fontSize: 12, color: "#1D4ED8", letterSpacing: 0.8, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {client.gstin}
                </div>
              </div>

              {/* Period */}
              <div>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.txM, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>Period</label>
                <select
                  value={period.value}
                  onChange={e => setPeriod(MOCK_PERIODS.find(p => p.value === e.target.value))}
                  style={{ width: "100%", padding: "9px 12px", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, color: t.tx, fontSize: 13, fontFamily: "inherit" }}>
                  {MOCK_PERIODS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              {/* Run */}
              <button onClick={run} style={{
                background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                color: "#fff", border: "none", borderRadius: 8,
                padding: "10px 24px", fontSize: 13, fontWeight: 700,
                cursor: "pointer", whiteSpace: "nowrap", width: isMob ? "100%" : "auto",
              }}>Run Recon →</button>
            </div>

            {/* GSTR-9 annual recon shortcut */}
            <GSTR9Panel client={client} t={t} isMob={isMob} />

            <div style={{ marginTop: 10, padding: "8px 12px", background: "#EFF6FF", borderRadius: 6, display: "flex", flexWrap: "wrap", gap: isMob ? 6 : 16 }}>
              {[
                ["Sources", "Tally ERP + GST Portal (GSTR-2B)"],
                ["Match", "GSTIN exact + fuzzy name 75%"],
                ["API", "POST /api/v1/ca/reconcile/gst"],
              ].map(([k, v]) => (
                <span key={k} style={{ fontSize: 11, fontFamily: "monospace", color: "#1D4ED8" }}>
                  <span style={{ color: "#3B82F6", fontWeight: 600 }}>{k}: </span>{v}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Running — agent step trace */}
        {phase === "running" && (
          <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, overflow: "hidden", marginBottom: 20 }}>
            <div style={{ padding: "11px 16px", background: t.sf2, borderBottom: `1px solid ${t.bd}`, display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#F59E0B", animation: "pulse 1s infinite" }} />
              <span style={{ fontFamily: "monospace", fontSize: 12, color: "#F59E0B", fontWeight: 600 }}>AGENT RUNNING</span>
              <span style={{ color: t.txM, fontSize: 12 }}>{client.name} · {period.label}</span>
            </div>
            <div style={{ padding: "6px 0" }}>
              {steps.map((s, i) => (
                <AgentStepRow key={i} label={s.label} type={s.type} status={s.status} ms={s.ms} t={t} />
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        {phase === "done" && result && (
          <>
            {/* Back + client info */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <button onClick={reset} style={{ background: "none", border: `1px solid ${t.bd}`, borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", color: t.txM }}>← New</button>
                <span style={{ fontSize: 14, fontWeight: 700, color: t.tx }}>{client.name}</span>
                <span style={{ fontFamily: "monospace", fontSize: 12, color: "#1D4ED8" }}>{client.gstin}</span>
                <span style={{ fontSize: 12, color: t.txM }}>·</span>
                <span style={{ fontSize: 13, color: t.txM }}>{period.label}</span>
              </div>
            </div>

            {/* Stat cards */}
            <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr 1fr" : "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
              <StatCard label="Tally Purchases" value={`₹${(result.summary.tally_amount / 100000).toFixed(1)}L`}
                sub={`${result.summary.tally_invoices} invoices`} accent="#3B82F6" t={t} />
              <StatCard label="GSTR-2B ITC" value={`₹${result.summary.gstr2b_itc.toLocaleString("en-IN")}`}
                sub={`${result.summary.gstr2b_invoices} invoices`} accent="#10B981" t={t} />
              <StatCard label="Match Rate" value={`${matchPct}%`}
                sub={`${result.matched.length} suppliers matched`} accent="#8B5CF6" t={t} />
              <StatCard label="ITC at Risk" value={`₹${totalRisk.toLocaleString("en-IN")}`}
                sub={`${result.mismatches.length} mismatches`} accent="#EF4444" t={t} />
            </div>

            {/* Tabs */}
            <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, overflow: "hidden" }}>
              {/* Tab bar — scrollable on mobile */}
              <div style={{ display: "flex", borderBottom: `1px solid ${t.bd}`, background: t.sf2, overflowX: "auto" }}>
                {[
                  { id: "mismatches", label: `⚠ Mismatches (${result.mismatches.length})`, color: "#EF4444" },
                  { id: "matched",    label: `✓ Matched (${result.matched.length})`,        color: "#10B981" },
                  { id: "trace",      label: "Agent Trace",                                 color: t.txM },
                ].map(({ id, label, color }) => (
                  <button key={id} onClick={() => setTab(id)} style={{
                    padding: "10px 16px", border: "none", cursor: "pointer",
                    background: "transparent", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                    color: tab === id ? color : t.txM,
                    borderBottom: `2px solid ${tab === id ? color : "transparent"}`,
                  }}>{label}</button>
                ))}
              </div>

              {/* Mismatches tab */}
              {tab === "mismatches" && (
                <>
                  <div style={{ padding: "10px 14px", background: "#FEF2F2", borderBottom: "1px solid #FECACA", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span>⚠️</span>
                    <span style={{ fontSize: 12, color: "#991B1B", fontWeight: 600 }}>
                      {result.mismatches.length} invoices in Tally not in GSTR-2B — ₹{totalRisk.toLocaleString("en-IN")} ITC at risk
                    </span>
                  </div>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isMob ? 680 : 820 }}>
                      <thead>
                        <tr style={{ borderBottom: `1px solid ${t.bd}`, background: t.sf2 }}>
                          {["Supplier", "Tally Amount", "ITC at Risk", "Reason", ""].map(h => (
                            <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.txM, letterSpacing: 0.6, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.mismatches.map((m, i) => (
                          <tr key={i} style={{ borderBottom: `1px solid ${t.bd2}`, background: i % 2 ? t.sf2 : "transparent" }}>
                            <td style={{ padding: "10px 14px", fontWeight: 600, fontSize: 13 }}>{m.supplier}</td>
                            <td style={{ padding: "10px 14px", fontFamily: "monospace", fontSize: 12 }}>₹{m.tally_amt.toLocaleString("en-IN")}</td>
                            <td style={{ padding: "10px 14px" }}>
                              <span style={{ fontFamily: "monospace", fontSize: 13, color: "#EF4444", fontWeight: 700 }}>₹{m.itc_risk.toLocaleString("en-IN")}</span>
                            </td>
                            <td style={{ padding: "10px 14px", fontSize: 11, color: t.txM }}>{m.reason}</td>
                            <td style={{ padding: "10px 14px" }}>
                              <button
                                onClick={() => alert(`WhatsApp draft for ${m.supplier}:\n\nDear ${m.supplier},\nRequest you to upload GSTR-1 for ${period.label}. ITC of ₹${m.itc_risk.toLocaleString("en-IN")} is not reflecting in our GSTR-2B.\n\nRegards`)}
                                style={{ background: "#F0FDF4", color: "#16A34A", border: "1px solid #BBF7D0", borderRadius: 5, padding: "3px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                                WhatsApp
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: "#FEF2F2", borderTop: "1px solid #FECACA" }}>
                          <td style={{ padding: "9px 14px", fontWeight: 700, fontSize: 12, color: "#991B1B" }}>Total ITC at Risk</td>
                          <td style={{ padding: "9px 14px", fontFamily: "monospace", fontSize: 12, color: t.txM }}>
                            ₹{result.mismatches.reduce((s, m) => s + m.tally_amt, 0).toLocaleString("en-IN")}
                          </td>
                          <td colSpan={3} style={{ padding: "9px 14px" }}>
                            <span style={{ fontFamily: "monospace", fontSize: 15, color: "#EF4444", fontWeight: 800 }}>
                              ₹{totalRisk.toLocaleString("en-IN")}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}

              {/* Matched tab */}
              {tab === "matched" && (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isMob ? 400 : "auto" }}>
                    <thead>
                      <tr style={{ borderBottom: `1px solid ${t.bd}`, background: t.sf2 }}>
                        {["Supplier", "Tally Amount", "2B ITC", "Diff", "Status"].map(h => (
                          <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.txM, letterSpacing: 0.6, textTransform: "uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.matched.map((m, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${t.bd2}` }}>
                          <td style={{ padding: "9px 14px", fontSize: 13 }}>{m.supplier}</td>
                          <td style={{ padding: "9px 14px", fontFamily: "monospace", fontSize: 12, color: t.tx2 }}>₹{m.tally_amt.toLocaleString("en-IN")}</td>
                          <td style={{ padding: "9px 14px", fontFamily: "monospace", fontSize: 12, color: "#10B981" }}>₹{m.itc.toLocaleString("en-IN")}</td>
                          <td style={{ padding: "9px 14px", fontFamily: "monospace", fontSize: 12, color: m.diff > 0 ? "#F59E0B" : t.txD }}>
                            {m.diff > 0 ? `₹${m.diff}` : "—"}
                          </td>
                          <td style={{ padding: "9px 14px" }}><StatusPill status={m.diff > 0 ? "pending" : "filed"} small /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Agent trace tab */}
              {tab === "trace" && (
                <div style={{ padding: "6px 0" }}>
                  {steps.map((s, i) => (
                    <AgentStepRow key={i} label={s.label} type={s.type} status="completed" ms={s.ms} t={t} />
                  ))}
                  <div style={{ padding: "10px 14px", borderTop: `1px solid ${t.bd}` }}>
                    <span style={{ fontFamily: "monospace", fontSize: 11, color: t.txD }}>
                      Total: {steps.reduce((s, st) => s + st.ms, 0)}ms · 7 steps · GST Reconciliation Agent
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}} @keyframes pulse{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// PAGE 2: COMPLIANCE CALENDAR
// ═══════════════════════════════════════════════════════════════

export function CompliancePage({ bp, t, go }) {
  const [items, setItems] = useState(MOCK_COMPLIANCE);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const isMob = bp.isMobile;

  const refresh = async () => {
    setLoading(true);
    const data = await caApi.getComplianceCalendar(true);
    if (data?.structured_calendar?.critical || data?.structured_calendar?.urgent) {
      // Flatten API response into our format
      const all = [
        ...(data.structured_calendar.critical || []).map(i => ({ ...i, status: "overdue" })),
        ...(data.structured_calendar.urgent || []).map(i => ({ ...i, status: "pending" })),
        ...(data.structured_calendar.this_month || []).map(i => ({ ...i, status: "upcoming" })),
      ];
      if (all.length) setItems(all);
    }
    setLoading(false);
  };

  const filtered = filter === "all" ? items : items.filter(i => i.status === filter);

  const counts = {
    overdue: items.filter(i => i.status === "overdue").length,
    pending: items.filter(i => i.status === "pending").length,
    notice:  items.filter(i => i.status === "notice").length,
    filed:   items.filter(i => i.status === "filed").length,
  };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: isMob ? 16 : 24 }}>
      <div style={{ maxWidth: 1060, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: isMob ? "flex-start" : "center", justifyContent: "space-between", flexDirection: isMob ? "column" : "row", gap: 12, marginBottom: 20 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 22 }}>📅</span>
              <h1 style={{ margin: 0, fontSize: isMob ? 18 : 22, fontWeight: 800, color: t.tx }}>Compliance Calendar</h1>
            </div>
            <p style={{ margin: 0, color: t.txM, fontSize: 13 }}>All filings across GST, ITR, TDS, and notices</p>
          </div>
          <button
            onClick={refresh}
            disabled={loading}
            style={{
              background: loading ? t.sf2 : t.p,
              color: loading ? t.txM : "#fff",
              border: "none", borderRadius: 8, padding: "8px 16px",
              fontSize: 13, cursor: loading ? "default" : "pointer", fontWeight: 600,
              display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
            }}>
            {loading ? "Scanning…" : "🤖 Run Compliance Agent"}
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
          {[
            { label: "Overdue", count: counts.overdue, color: "#EF4444", bg: "#FEF2F2", status: "overdue" },
            { label: "Pending",  count: counts.pending,  color: "#F59E0B", bg: "#FFF7ED", status: "pending"  },
            { label: "Notices",  count: counts.notice,   color: "#8B5CF6", bg: "#F5F3FF", status: "notice"   },
            { label: "Filed",    count: counts.filed,    color: "#10B981", bg: "#F0FDF4", status: "filed"    },
          ].map(({ label, count, color, bg, status: s }) => (
            <button
              key={label}
              onClick={() => setFilter(filter === s ? "all" : s)}
              style={{
                background: filter === s ? bg : t.sf,
                border: `1px solid ${filter === s ? color + "40" : t.bd}`,
                borderTop: `3px solid ${color}`,
                borderRadius: 10, padding: isMob ? "10px 8px" : "12px 16px",
                cursor: "pointer", textAlign: "left",
              }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: t.txM, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color, fontVariantNumeric: "tabular-nums" }}>{count}</div>
            </button>
          ))}
        </div>

        {/* Filter pills */}
        <div style={{ display: "flex", gap: 8, marginBottom: 14, overflowX: "auto", paddingBottom: 2 }}>
          {[["all", "All"], ["overdue", "Overdue"], ["pending", "Pending"], ["notice", "Notices"], ["filed", "Filed"], ["upcoming", "Upcoming"]].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} style={{
              background: filter === v ? t.p : t.sf,
              color: filter === v ? "#fff" : t.txM,
              border: `1px solid ${filter === v ? t.p : t.bd}`,
              borderRadius: 20, padding: "5px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap",
            }}>{l}</button>
          ))}
        </div>

        {/* Table / Card view */}
        {filtered.length === 0 ? (
          <EmptyState icon="✅" title="All clear!" sub="No compliance items match this filter." t={t} />
        ) : isMob ? (
          // Mobile: Card stack
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filtered.map((item, i) => (
              <div key={i} style={{
                background: t.sf, border: `1px solid ${t.bd}`,
                borderLeft: `4px solid ${item.status === "overdue" ? "#EF4444" : item.status === "notice" ? "#8B5CF6" : item.status === "pending" ? "#F59E0B" : "#10B981"}`,
                borderRadius: 10, padding: "12px 14px",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: t.tx }}>{item.client}</div>
                  <StatusPill status={item.status} small />
                </div>
                <div style={{ fontSize: 12, color: t.tx2, marginBottom: 6 }}>{item.task}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                  {item.type && <TypeBadge type={item.type} t={t} />}
                  <span style={{ fontSize: 11, color: item.days < 0 ? "#EF4444" : item.days != null && item.days <= 7 ? "#F59E0B" : t.txM, fontWeight: 600 }}>
                    {item.days == null ? "✓ Filed" : item.days < 0 ? `${Math.abs(item.days)}d late` : `Due ${item.due}`}
                  </span>
                  {item.penalty > 0 && (
                    <span style={{ fontFamily: "monospace", fontSize: 10, color: "#EF4444", fontWeight: 700 }}>
                      +₹{item.penalty.toLocaleString("en-IN")} penalty
                    </span>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {item.gstin && <span style={{ fontFamily: "monospace", fontSize: 9, color: "#1D4ED8", background: "#EFF6FF", padding: "2px 6px", borderRadius: 3 }}>{item.gstin}</span>}
                  {item.arn && <span style={{ fontFamily: "monospace", fontSize: 9, color: "#10B981", background: "#F0FDF4", padding: "2px 6px", borderRadius: 3 }}>ARN: {item.arn}</span>}
                </div>
                {item.status !== "filed" && (
                  <button
                    onClick={() => go && go(item.type === "Notice" ? "notices" : "chat")}
                    style={{ marginTop: 10, background: t.pS, color: t.p, border: `1px solid ${t.p}30`, borderRadius: 6, padding: "5px 12px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                    {item.status === "notice" ? "Draft Reply" : "Take Action"} →
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          // Desktop: Table
          <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, overflow: "hidden" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: t.sf2, borderBottom: `1px solid ${t.bd}` }}>
                  {["Client", "GSTIN / PAN", "Task", "Type", "Due / Penalty", "ARN", "Status", "Action"].map(h => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: 10, fontWeight: 700, color: t.txM, letterSpacing: 0.6, textTransform: "uppercase" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${t.bd2}`, cursor: "default" }}
                    onMouseEnter={e => e.currentTarget.style.background = t.sfH}
                    onMouseLeave={e => e.currentTarget.style.background = ""}>
                    <td style={{ padding: "10px 12px", fontWeight: 700, fontSize: 12 }}>{item.client}</td>
                    <td style={{ padding: "10px 12px" }}>
                      {item.gstin && <div style={{ fontFamily: "monospace", fontSize: 10, color: "#1D4ED8", letterSpacing: 0.4 }}>{item.gstin}</div>}
                      {item.pan  && <div style={{ fontFamily: "monospace", fontSize: 10, color: t.txD, marginTop: 1 }}>{item.pan}</div>}
                    </td>
                    <td style={{ padding: "10px 12px", fontSize: 12, color: t.tx2 }}>{item.task}</td>
                    <td style={{ padding: "10px 12px" }}>{item.type && <TypeBadge type={item.type} t={t} />}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: item.days < 0 ? "#EF4444" : item.days != null && item.days <= 7 ? "#F59E0B" : t.txM }}>
                        {item.days == null ? "✓ Filed" : item.days < 0 ? `${Math.abs(item.days)}d overdue` : item.due}
                      </div>
                      {item.penalty > 0 && (
                        <div style={{ fontFamily: "monospace", fontSize: 10, color: "#EF4444", marginTop: 2 }}>
                          +₹{item.penalty.toLocaleString("en-IN")} penalty
                        </div>
                      )}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      {item.arn
                        ? <span style={{ fontFamily: "monospace", fontSize: 10, color: "#10B981", background: "#F0FDF4", padding: "2px 6px", borderRadius: 3 }}>{item.arn}</span>
                        : <span style={{ color: t.txD, fontSize: 11 }}>—</span>}
                    </td>
                    <td style={{ padding: "10px 12px" }}><StatusPill status={item.status} small /></td>
                    <td style={{ padding: "10px 12px" }}>
                      {item.status !== "filed" && (
                        <button
                          onClick={() => go && go(item.status === "notice" ? "notices" : "chat")}
                          style={{ background: t.pS, color: t.p, border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>
                          {item.status === "notice" ? "Draft Reply" : "Action"} →
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// PAGE 3: NOTICES
// ═══════════════════════════════════════════════════════════════

export function NoticesPage({ bp, t }) {
  const [notices, setNotices] = useState(MOCK_NOTICES);
  const [selected, setSelected] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [draftResult, setDraftResult] = useState(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [clientForUpload, setClientForUpload] = useState(MOCK_CLIENTS[0].name);
  const fileRef = useRef(null);
  const isMob = bp.isMobile;

  const uploadNotice = async (file) => {
    setUploading(true);
    const data = await caApi.uploadNotice(file, clientForUpload);
    if (data?.agent_result?.output?.notice_classification) {
      const cls = data.agent_result.output.notice_classification;
      const newNotice = {
        id: `n${Date.now()}`,
        client: clientForUpload,
        pan: cls.pan_or_gstin || "—",
        type: cls.notice_type || "OTHER",
        section: cls.section || "—",
        subject: cls.subject || file.name,
        issued: cls.notice_date || new Date().toLocaleDateString("en-IN"),
        due: cls.response_due_date || "—",
        daysLeft: 14,
        status: "pending",
        demand: cls.demand_amount || null,
        draft: data.agent_result.output.draft_reply,
        checklist: data.agent_result.output.document_checklist,
      };
      setNotices(n => [newNotice, ...n]);
      setSelected(newNotice);
    } else {
      // Mock: add placeholder
      const newNotice = {
        id: `n${Date.now()}`, client: clientForUpload, pan: "—",
        type: "OTHER", section: "—", subject: file.name,
        issued: new Date().toLocaleDateString("en-IN"), due: "—",
        daysLeft: 14, status: "pending", demand: null,
      };
      setNotices(n => [newNotice, ...n]);
      setSelected(newNotice);
    }
    setUploading(false);
  };

  const draftReply = async (notice) => {
    setDraftLoading(true);
    setDraftResult(null);
    // Simulate agent running
    await new Promise(r => setTimeout(r, 2800));
    setDraftResult({
      draft: `To,\nThe Assessing Officer\n${notice.section.includes("GST") || notice.section.includes("73") ? "CGST Office" : "Income Tax Office"}\n\nSub: Reply to Notice u/s ${notice.section} dated ${notice.issued} — ${notice.client}\n\nRespected Sir/Madam,\n\nWith reference to your notice dated ${notice.issued} bearing no. [NOTICE_NO], we hereby submit our reply on behalf of our client ${notice.client}:\n\n1. Our client has duly complied with all statutory obligations under the applicable provisions.\n\n2. We enclose the following documents in support of our submission:\n   a) Books of accounts for the relevant period\n   b) Copy of ITR/Return filed (if applicable)\n   c) Form 26AS TDS certificate\n   d) Bank statements evidencing payments made\n\n3. The alleged discrepancy, if any, has been due to timing differences which are explained in the enclosed computation.\n\nIn view of the above, we request you to kindly drop the proceedings and accept our reply.\n\nWe remain at your service for any further clarifications.\n\nYours faithfully,\n[CA Firm Name]\nChartered Accountants\n\n⚠️  Review before submission: Verify document references and fill [NOTICE_NO]`,
      checklist: ["Books of accounts", "ITR acknowledgement", "Form 26AS", "Bank statements", "Computation sheet"],
    });
    setDraftLoading(false);
  };

  const urgentCount = notices.filter(n => n.status === "pending" && n.daysLeft != null && n.daysLeft <= 7).length;
  const overdueCount = notices.filter(n => n.status === "overdue").length;

  return (
    <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: isMob ? 16 : 24 }}>
        <div style={{ maxWidth: 1060, margin: "0 auto" }}>

          {/* Header */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 22 }}>⚖️</span>
              <h1 style={{ margin: 0, fontSize: isMob ? 18 : 22, fontWeight: 800, color: t.tx }}>Notice Manager</h1>
              {urgentCount > 0 && (
                <span style={{ background: "#FEE2E2", color: "#991B1B", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                  {urgentCount} due this week
                </span>
              )}
              {overdueCount > 0 && (
                <span style={{ background: "#FEF2F2", color: "#EF4444", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>
                  {overdueCount} overdue
                </span>
              )}
            </div>
            <p style={{ margin: 0, color: t.txM, fontSize: 13 }}>Upload notices for AI-drafted replies. Tracks GST SCN, IT notices, and TDS demands.</p>
          </div>

          {/* Upload panel */}
          <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, padding: isMob ? 16 : 20, marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: isMob ? "flex-start" : "center", gap: 14, flexDirection: isMob ? "column" : "row", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: isMob ? "100%" : 200 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: t.txM, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>Client for this notice</label>
                <select value={clientForUpload} onChange={e => setClientForUpload(e.target.value)}
                  style={{ width: "100%", padding: "8px 12px", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, color: t.tx, fontSize: 13, fontFamily: "inherit" }}>
                  {MOCK_CLIENTS.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                </select>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 6, width: isMob ? "100%" : "auto" }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.txM, textTransform: "uppercase", letterSpacing: 0.5 }}>Upload Notice PDF</label>
                <div
                  onClick={() => !uploading && fileRef.current?.click()}
                  style={{
                    border: `2px dashed ${t.bd}`, borderRadius: 8,
                    padding: "16px 24px", textAlign: "center", cursor: uploading ? "default" : "pointer",
                    background: uploading ? t.sf2 : "transparent",
                    transition: "all .2s", minWidth: isMob ? "100%" : 240,
                  }}
                  onMouseEnter={e => { if (!uploading) e.currentTarget.style.borderColor = t.p; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = t.bd; }}>
                  <input ref={fileRef} type="file" accept=".pdf" style={{ display: "none" }}
                    onChange={e => { if (e.target.files[0]) uploadNotice(e.target.files[0]); }} />
                  {uploading ? (
                    <span style={{ fontSize: 13, color: t.txM }}>🔄 Processing notice…</span>
                  ) : (
                    <span style={{ fontSize: 13, color: t.txM }}>📄 Click to upload PDF or drag here</span>
                  )}
                </div>
              </div>

              <div style={{ fontSize: 11, color: t.txD, maxWidth: 220 }}>
                Supports: GST SCN, IT 143(1)/142(1)/148, TRACES TDS demands. AI will classify and draft reply.
              </div>
            </div>
          </div>

          {/* Two-column on desktop, stacked on mobile */}
          <div style={{ display: "grid", gridTemplateColumns: selected && !isMob ? "1fr 1fr" : "1fr", gap: 16 }}>
            {/* Notice list */}
            <div style={{ display: "flex", flexDirection: "column", gap: 0, background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", background: t.sf2, borderBottom: `1px solid ${t.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: t.tx }}>All Notices ({notices.length})</span>
              </div>
              {notices.length === 0 ? (
                <EmptyState icon="📭" title="No notices" sub="Upload a notice PDF to get started" t={t} />
              ) : (
                notices.map((n, i) => (
                  <div key={n.id}
                    onClick={() => setSelected(selected?.id === n.id ? null : n)}
                    style={{
                      padding: "13px 16px",
                      borderBottom: i < notices.length - 1 ? `1px solid ${t.bd2}` : "none",
                      cursor: "pointer",
                      background: selected?.id === n.id ? t.sfH : "transparent",
                      borderLeft: `3px solid ${n.status === "overdue" ? "#EF4444" : n.daysLeft != null && n.daysLeft <= 7 ? "#F59E0B" : "#8B5CF6"}`,
                      transition: "background .15s",
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 4 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: t.tx, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{n.client}</div>
                      <StatusPill status={n.status} small />
                    </div>
                    <div style={{ fontSize: 12, color: t.tx2, marginBottom: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      u/s {n.section} — {n.subject}
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <TypeBadge type={n.type === "IT_142_1" || n.type === "IT_143_1" || n.type === "IT_148" ? "IT" : n.type === "GST_SCN" ? "GST" : "TDS"} t={t} />
                      {n.demand && (
                        <span style={{ fontFamily: "monospace", fontSize: 11, color: "#EF4444", fontWeight: 600 }}>
                          ₹{n.demand.toLocaleString("en-IN")}
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: n.daysLeft < 0 ? "#EF4444" : n.daysLeft != null && n.daysLeft <= 7 ? "#F59E0B" : t.txD, fontWeight: 600 }}>
                        {n.daysLeft == null ? `Due ${n.due}` : n.daysLeft < 0 ? `${Math.abs(n.daysLeft)}d overdue` : `${n.daysLeft}d left`}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Detail panel */}
            {selected && (
              <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, overflow: "hidden", display: "flex", flexDirection: "column" }}>
                <div style={{ padding: "12px 16px", background: t.sf2, borderBottom: `1px solid ${t.bd}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: t.tx }}>Notice Detail</span>
                  <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", color: t.txM, cursor: "pointer", fontSize: 16, padding: 2 }}>✕</button>
                </div>

                <div style={{ padding: 16, flex: 1, overflowY: "auto" }}>
                  {/* Meta */}
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                    {[
                      ["Client", selected.client],
                      ["PAN", selected.pan],
                      ["Section", selected.section],
                      ["Issued", selected.issued],
                      ["Due", selected.due],
                      ["Demand", selected.demand ? `₹${selected.demand.toLocaleString("en-IN")}` : "—"],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: t.txM, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 2 }}>{k}</div>
                        <div style={{ fontSize: 13, color: t.tx, fontFamily: k === "PAN" ? "monospace" : "inherit" }}>{v}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: t.txM, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 4 }}>Subject</div>
                    <div style={{ fontSize: 13, color: t.tx }}>{selected.subject}</div>
                  </div>

                  {/* Draft reply button */}
                  {!draftResult && !draftLoading && (
                    <button
                      onClick={() => draftReply(selected)}
                      style={{
                        width: "100%", background: "linear-gradient(135deg, #6366F1, #8B5CF6)",
                        color: "#fff", border: "none", borderRadius: 8, padding: "11px",
                        fontSize: 13, fontWeight: 700, cursor: "pointer",
                      }}>
                      ✨ Draft Reply with AI
                    </button>
                  )}

                  {draftLoading && (
                    <div style={{ padding: "20px 0", textAlign: "center" }}>
                      <div style={{ fontSize: 13, color: t.txM, marginBottom: 8 }}>🤖 Notice Response Agent running…</div>
                      <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                        {["Classifying notice", "Cross-referencing data", "Drafting reply"].map((s, i) => (
                          <span key={s} style={{ fontSize: 11, color: t.txD, padding: "3px 8px", background: t.sf2, borderRadius: 4 }}>
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {draftResult && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#10B981", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>✅ Draft Reply</div>
                      <pre style={{
                        background: t.sf2, border: `1px solid ${t.bd}`,
                        borderRadius: 8, padding: 14, fontSize: 11,
                        color: t.tx, whiteSpace: "pre-wrap", lineHeight: 1.7,
                        maxHeight: 300, overflowY: "auto", margin: 0, fontFamily: "monospace",
                      }}>{draftResult.draft}</pre>

                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: t.txM, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>Document Checklist</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                          {draftResult.checklist.map((item, i) => (
                            <label key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: t.tx, cursor: "pointer" }}>
                              <input type="checkbox" style={{ accentColor: t.p }} />
                              {item}
                            </label>
                          ))}
                        </div>
                      </div>

                      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
                        <button style={{ background: t.pS, color: t.p, border: `1px solid ${t.p}30`, borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                          📋 Copy
                        </button>
                        <button style={{ background: "#D1FAE5", color: "#065F46", border: "1px solid #A7F3D0", borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                          💬 Send via WhatsApp
                        </button>
                        <button onClick={() => { setDraftResult(null); setDraftLoading(false); }}
                          style={{ background: "none", border: `1px solid ${t.bd}`, borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer", color: t.txM, fontWeight: 600 }}>
                          Regenerate
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// DOCUMENTS PAGE — Engagement Letters, Computation Sheets, 3CD
// ═══════════════════════════════════════════════════════════════

const CA_SERVICES = [
  { id: "gst",         label: "GST Compliance",       icon: "🏛️" },
  { id: "itr",         label: "Income Tax Return",     icon: "📋" },
  { id: "audit",       label: "Tax Audit (Form 3CD)",  icon: "🔍" },
  { id: "tds",         label: "TDS Compliance",        icon: "🧾" },
  { id: "roc",         label: "ROC / MCA Compliance",  icon: "🏢" },
  { id: "bookkeeping", label: "Bookkeeping & Accounts",icon: "📒" },
  { id: "advisory",    label: "Business Advisory",     icon: "💡" },
];

export function DocumentsPage({ bp, t }) {
  const isMob = bp.isMobile;

  // Tab state
  const [activeDoc, setActiveDoc] = useState("engagement");

  // Form state — shared
  const [clientName, setClientName]   = useState("");
  const [pan,        setPan]          = useState("");
  const [gstin,      setGstin]        = useState("");
  const [fy,         setFy]           = useState("2024-25");
  const [selectedSvc,setSelectedSvc]  = useState(["gst", "itr"]);
  const [fees,       setFees]         = useState("");
  const [caFirm,     setCaFirm]       = useState("");
  const [caName,     setCaName]       = useState("");
  const [membership, setMembership]   = useState("");
  const [loading,    setLoading]      = useState(false);
  const [error,      setError]        = useState(null);
  const [generated,  setGenerated]    = useState(null);

  // Computation sheet state
  const [entityType,   setEntityType]   = useState("company");
  const [grossRev,     setGrossRev]     = useState("");
  const [otherInc,     setOtherInc]     = useState("");
  const [totalExp,     setTotalExp]     = useState("");
  const [depreciation, setDepreciation] = useState("");
  const [disallow,     setDisallow]     = useState("");
  const [deductions,   setDeductions]   = useState("");
  const [tdsCredit,    setTdsCredit]    = useState("");
  const [advTax,       setAdvTax]       = useState("");

  // Form 3CD state
  const [entityAddress,  setEntityAddress]  = useState("");
  const [principalBiz,   setPrincipalBiz]   = useState("");
  const [firmFRN,        setFirmFRN]        = useState("");

  const FY_OPTIONS = ["2024-25", "2023-24", "2022-23", "2025-26"];

  const toggleSvc = (id) =>
    setSelectedSvc(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );

  const feeNum  = parseFloat(fees.replace(/,/g, "")) || 0;
  const gstAmt  = Math.round(feeNum * 0.18);
  const total   = feeNum + gstAmt;

  const numVal = s => parseFloat(String(s).replace(/,/g, "")) || 0;
  const canGenerate = activeDoc === "engagement"
    ? (clientName.trim() && pan.length === 10 && selectedSvc.length > 0 && feeNum > 0 && caFirm.trim() && caName.trim() && membership.trim())
    : activeDoc === "computation"
    ? (clientName.trim() && pan.length === 10 && numVal(grossRev) > 0)
    : (clientName.trim() && pan.length === 10);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setGenerated(null);

    const endpoint = activeDoc === "computation"
      ? "/api/v1/ca/documents/computation-sheet"
      : activeDoc === "3cd"
      ? "/api/v1/ca/documents/form3cd"
      : "/api/v1/ca/documents/engagement-letter";

    const body = activeDoc === "computation" ? {
      client_name:    clientName,
      pan:            pan.toUpperCase(),
      fy,
      entity_type:    entityType,
      gross_revenue:  numVal(grossRev),
      other_income:   numVal(otherInc),
      total_expenses: numVal(totalExp),
      depreciation:   numVal(depreciation),
      disallowances:  numVal(disallow),
      deductions:     numVal(deductions),
      tds_credit:     numVal(tdsCredit),
      advance_tax:    numVal(advTax),
      ca_firm:        caFirm,
      ca_name:        caName,
      membership_no:  membership,
    } : activeDoc === "3cd" ? {
      client_name:         clientName,
      pan:                 pan.toUpperCase(),
      fy,
      entity_type:         entityType,
      gross_revenue:       numVal(grossRev),
      registered_address:  entityAddress,
      principal_business:  principalBiz,
      ca_firm:             caFirm,
      ca_name:             caName,
      membership_no:       membership,
      frn:                 firmFRN,
    } : {
      client_name:    clientName,
      pan:            pan.toUpperCase(),
      gstin:          gstin || null,
      fy,
      services:       selectedSvc,
      fees_total:     feeNum,
      ca_firm:        caFirm,
      ca_name:        caName,
      membership_no:  membership,
    };

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("cosfy_access") || ""}`,
        },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Generation failed" }));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }

      // Stream blob download
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const safe = clientName.replace(/\s+/g, "_");
      const name = activeDoc === "computation"
        ? `Computation_Sheet_${safe}_AY${fy.replace("-","")}.docx`
        : activeDoc === "3cd"
        ? `Form3CD_${safe}_AY${fy.replace("-","")}.docx`
        : `Engagement_Letter_${safe}_${fy}.docx`;
      setGenerated({ url, name });

    } catch (e) {
      // Demo mode — show success with mock file info
      if (e.message.includes("fetch") || e.message.includes("Failed")) {
        const demoName = activeDoc === "computation"
          ? `Computation_Sheet_${clientName.replace(/\s+/g, "_")}_AY${fy.replace("-","")}.docx`
          : activeDoc === "3cd"
          ? `Form3CD_${clientName.replace(/\s+/g, "_")}_AY${fy.replace("-","")}.docx`
          : `Engagement_Letter_${clientName.replace(/\s+/g, "_")}_${fy}.docx`;
        setGenerated({ url: null, name: demoName, demo: true });
      } else {
        setError(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!generated?.url) return;
    const a = document.createElement("a");
    a.href    = generated.url;
    a.download = generated.name;
    a.click();
  };

  const inp = (val, onChange, placeholder, mono, maxLen) => (
    <input
      value={val}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      maxLength={maxLen}
      style={{
        width: "100%", padding: "9px 12px",
        background: t.sf2, border: `1px solid ${t.bd}`,
        borderRadius: 8, fontSize: 13, color: t.tx,
        fontFamily: mono ? "monospace" : "inherit",
        outline: "none", boxSizing: "border-box",
        letterSpacing: mono ? 0.8 : "normal",
      }}
    />
  );

  const lbl = (text, required) => (
    <label style={{ display: "block", fontSize: 10, fontWeight: 700, color: t.txM, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
      {text}{required && <span style={{ color: "#EF4444", marginLeft: 2 }}>*</span>}
    </label>
  );

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: isMob ? 16 : 24 }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
            <span style={{ fontSize: 22 }}>📄</span>
            <h1 style={{ margin: 0, fontSize: isMob ? 18 : 22, fontWeight: 800, color: t.tx }}>
              CA Documents
            </h1>
          </div>
          <p style={{ margin: 0, color: t.txM, fontSize: 13 }}>
            Generate ICAI-compliant engagement letters, computation sheets, and 3CD reports
          </p>
        </div>

        {/* Doc type tabs */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, overflowX: "auto", paddingBottom: 2 }}>
          {[
            { id: "engagement",  label: "Engagement Letter", icon: "✍️", live: true  },
            { id: "computation", label: "Computation Sheet",  icon: "🧮", live: true  },
            { id: "3cd",         label: "Form 3CD (3CA/3CB)", icon: "📊", live: true  },
          ].map(doc => (
            <div key={doc.id}
              onClick={() => doc.live && setActiveDoc(doc.id)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 16px", borderRadius: 8,
                cursor: doc.live ? "pointer" : "not-allowed",
                background: activeDoc === doc.id ? t.pS : t.sf,
                border: `1px solid ${activeDoc === doc.id ? t.p + "40" : t.bd}`,
                color: activeDoc === doc.id ? t.p : doc.live ? t.tx2 : t.txD,
                fontSize: 13, fontWeight: activeDoc === doc.id ? 700 : 500, whiteSpace: "nowrap",
                transition: "all .15s",
              }}>
              {doc.icon} {doc.label}
              {!doc.live && <span style={{ fontSize: 9, background: t.bd, padding: "1px 5px", borderRadius: 3, marginLeft: 4 }}>Soon</span>}
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 340px", gap: 20 }}>

          {/* ── LEFT: Form ── */}
          <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, padding: isMob ? 16 : 24 }}>
            <div style={{ fontWeight: 700, fontSize: 15, color: t.tx, marginBottom: 20 }}>
              {activeDoc === "engagement" ? "✍️ Engagement Letter" : activeDoc === "computation" ? "🧮 Computation Sheet" : "📊 Form 3CD — Tax Audit Report"}
            </div>

            {/* Client details */}
            <div style={{ fontSize: 11, fontWeight: 700, color: t.p, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${t.bd}` }}>
              Client Details
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 16 }}>
              <div>
                {lbl("Client / Firm Name", true)}
                {inp(clientName, setClientName, "Sharma Trading Co.")}
              </div>
              <div>
                {lbl("PAN", true)}
                {inp(pan, v => setPan(v.toUpperCase()), "AABCS1429B", true, 10)}
              </div>
              <div>
                {lbl("GSTIN")}
                {inp(gstin, v => setGstin(v.toUpperCase()), "27AABCS1429B1ZS", true, 15)}
              </div>
              <div>
                {lbl("Financial Year", true)}
                <select value={fy} onChange={e => setFy(e.target.value)}
                  style={{ width: "100%", padding: "9px 12px", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, color: t.tx, fontSize: 13, outline: "none" }}>
                  {FY_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>

            {/* ── COMPUTATION SHEET FIELDS (shown when computation tab active) ── */}
            {activeDoc === "computation" && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${t.bd}` }}>
                  Entity & Income Details
                </div>
                <div style={{ marginBottom: 14 }}>
                  {lbl("Entity Type", true)}
                  <select value={entityType} onChange={e => setEntityType(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, color: t.tx, fontSize: 13, outline: "none" }}>
                    {[["individual","Individual"],["huf","HUF"],["firm","Partnership Firm"],["company","Company"],["llp","LLP"]].map(([v,l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 16 }}>
                  {[
                    ["Gross Revenue / Turnover",   grossRev,     setGrossRev,     true],
                    ["Other Income",               otherInc,     setOtherInc,     false],
                    ["Total Business Expenditure", totalExp,     setTotalExp,     true],
                    ["Depreciation u/s 32",         depreciation, setDepreciation, false],
                    ["Disallowances (40A3, 43B)",  disallow,     setDisallow,     false],
                    ["Chapter VI-A Deductions",    deductions,   setDeductions,   false],
                  ].map(([label, val, setter, req]) => (
                    <div key={label}>
                      {lbl(label, req)}
                      {inp(val, setter, "0", false)}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#059669", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${t.bd}` }}>
                  Pre-paid Taxes
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 16 }}>
                  {[
                    ["TDS Credit (Form 26AS)", tdsCredit, setTdsCredit],
                    ["Advance Tax Paid",        advTax,    setAdvTax],
                  ].map(([label, val, setter]) => (
                    <div key={label}>
                      {lbl(label)}
                      {inp(val, setter, "0", false)}
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* ── FORM 3CD FIELDS (shown when 3cd tab active) ── */}
            {activeDoc === "3cd" && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#065F46", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${t.bd}` }}>
                  Audit Details
                </div>
                <div style={{ marginBottom: 14 }}>
                  {lbl("Entity Type", true)}
                  <select value={entityType} onChange={e => setEntityType(e.target.value)}
                    style={{ width: "100%", padding: "9px 12px", background: t.sf2, border: `1px solid ${t.bd}`, borderRadius: 8, color: t.tx, fontSize: 13, outline: "none" }}>
                    {[["individual","Individual"],["huf","HUF"],["firm","Partnership Firm"],["company","Company"],["llp","LLP"]].map(([v,l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 16 }}>
                  <div>
                    {lbl("Gross Revenue (for 44AB threshold)")}
                    {inp(grossRev, setGrossRev, "8500000", false)}
                  </div>
                  <div>
                    {lbl("CA Firm Reg. No. (FRN)")}
                    {inp(firmFRN, setFirmFRN, "012345W", true)}
                  </div>
                </div>
                <div style={{ marginBottom: 14 }}>
                  {lbl("Registered Address")}
                  {inp(entityAddress, setEntityAddress, "123 MG Road, Mumbai 400001", false)}
                </div>
                <div style={{ marginBottom: 14 }}>
                  {lbl("Nature of Business / Profession")}
                  {inp(principalBiz, setPrincipalBiz, "Trading in goods / Manufacturing / Services", false)}
                </div>
                <div style={{ padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, marginBottom: 4 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#065F46", marginBottom: 4 }}>What you get</div>
                  <div style={{ fontSize: 11, color: "#047857", lineHeight: 1.6 }}>
                    All 50 clauses of Form 3CD with [FILL] markers on the 40 items requiring
                    client-specific data. Includes auditor declaration block and UDIN placeholder.
                    The CA completes [FILL] items and obtains UDIN from ICAI portal before filing.
                  </div>
                </div>
              </>
            )}

            {/* ── ENGAGEMENT LETTER FIELDS (shown when engagement tab active) ── */}
            {activeDoc === "engagement" && (<>
            {/* Services */}
            <div style={{ fontSize: 11, fontWeight: 700, color: t.p, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${t.bd}` }}>
              Services Covered
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr 1fr" : "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
              {CA_SERVICES.map(svc => {
                const on = selectedSvc.includes(svc.id);
                return (
                  <button key={svc.id} onClick={() => toggleSvc(svc.id)} style={{
                    display: "flex", alignItems: "center", gap: 6,
                    padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                    background: on ? t.pS : t.sf2,
                    border: `1px solid ${on ? t.p + "50" : t.bd}`,
                    color: on ? t.p : t.txM,
                    fontSize: 11, fontWeight: on ? 700 : 500, textAlign: "left",
                    transition: "all .15s",
                  }}>
                    <span style={{ fontSize: 14 }}>{svc.icon}</span>
                    <span style={{ flex: 1 }}>{svc.label}</span>
                    {on && <span style={{ fontSize: 10 }}>✓</span>}
                  </button>
                );
              })}
            </div>

            {/* Fees */}
            <div style={{ fontSize: 11, fontWeight: 700, color: t.p, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${t.bd}` }}>
              Professional Fees
            </div>
            <div style={{ marginBottom: 16 }}>
              {lbl("Total Fee (excl. GST) in ₹", true)}
              {inp(fees, setFees, "e.g. 150000")}
              {feeNum > 0 && (
                <div style={{ marginTop: 8, display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {[["Fee", `₹${feeNum.toLocaleString("en-IN")}`], ["GST @18%", `₹${gstAmt.toLocaleString("en-IN")}`], ["Total", `₹${total.toLocaleString("en-IN")}`]].map(([k, v]) => (
                    <div key={k} style={{ fontSize: 11, color: t.txM }}>
                      <span>{k}: </span>
                      <span style={{ fontFamily: "monospace", fontWeight: 700, color: t.tx }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            </>)}

            {/* CA Firm Details — shared for all document types */}
            <div style={{ fontSize: 11, fontWeight: 700, color: t.p, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${t.bd}` }}>
              CA Firm Details
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMob ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 20 }}>
              <div>
                {lbl("CA Firm Name", true)}
                {inp(caFirm, setCaFirm, "M/s Garg & Associates")}
              </div>
              <div>
                {lbl("Signing CA Name", true)}
                {inp(caName, setCaName, "CA Akshay Garg")}
              </div>
              <div>
                {lbl("ICAI Membership No.", true)}
                {inp(membership, setMembership, "123456", true)}
              </div>
            </div>

            {/* Error */}
            {error && (
              <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: "#991B1B" }}>
                ⚠️ {error}
              </div>
            )}

            {/* Generate button */}
            <button
              onClick={handleGenerate}
              disabled={!canGenerate || loading}
              style={{
                width: "100%", padding: "12px",
                background: (!canGenerate || loading) ? t.bd : "linear-gradient(135deg, #1847C8, #0EA5E9)",
                color: (!canGenerate || loading) ? t.txD : "#fff",
                border: "none", borderRadius: 10,
                fontSize: 14, fontWeight: 700, cursor: (!canGenerate || loading) ? "default" : "pointer",
                transition: "all .2s",
              }}>
              {loading ? "⏳ Generating document…"
               : activeDoc === "computation" ? "🧮 Generate Computation Sheet"
               : activeDoc === "3cd"         ? "📊 Generate Form 3CD"
               :                              "📄 Generate Engagement Letter"}
            </button>

            {!canGenerate && !loading && (
              <div style={{ textAlign: "center", fontSize: 11, color: t.txD, marginTop: 8 }}>
                Fill all required fields (*) to enable generation
              </div>
            )}
          </div>

          {/* ── RIGHT: Preview / Download ── */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Generated file */}
            {generated ? (
              <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 12, padding: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#065F46", marginBottom: 8 }}>
                  ✅ Document Ready
                </div>
                <div style={{ fontFamily: "monospace", fontSize: 11, color: "#047857", marginBottom: 16, wordBreak: "break-all" }}>
                  {generated.name}
                </div>

                {generated.demo ? (
                  <div style={{ fontSize: 12, color: "#6B7280", marginBottom: 12 }}>
                    Demo mode — connect backend to download real .docx file.
                  </div>
                ) : (
                  <button onClick={handleDownload} style={{
                    width: "100%", background: "#10B981", color: "#fff",
                    border: "none", borderRadius: 8, padding: "10px",
                    fontSize: 13, fontWeight: 700, cursor: "pointer",
                    marginBottom: 8,
                  }}>
                    ⬇ Download .docx
                  </button>
                )}

                <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.6 }}>
                  Open in Microsoft Word or Google Docs. Review all [VERIFY] placeholders before sending to client.
                </div>

                <button onClick={() => setGenerated(null)} style={{
                  marginTop: 12, background: "none", border: `1px solid #BBF7D0`,
                  borderRadius: 6, padding: "5px 12px", fontSize: 11,
                  cursor: "pointer", color: "#065F46", width: "100%",
                }}>
                  Generate Another
                </button>
              </div>
            ) : (
              <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, padding: 20 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: t.tx, marginBottom: 12 }}>📋 Document Preview</div>
                <div style={{ fontSize: 12, color: t.txM, lineHeight: 1.7 }}>
                  Your engagement letter will include:
                </div>
                <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    "Firm letterhead + reference number",
                    "Scope of services (itemised)",
                    "Professional fee table with GST",
                    "Client responsibilities (7 clauses)",
                    "Confidentiality clause",
                    "Limitation of liability",
                    "Independence declaration",
                    "Termination & governing law",
                    "Client acceptance signature block",
                  ].map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11, color: t.tx2 }}>
                      <span style={{ color: "#10B981", flexShrink: 0 }}>✓</span>
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Quick fill from clients */}
            <div style={{ background: t.sf, border: `1px solid ${t.bd}`, borderRadius: 12, padding: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: t.tx, marginBottom: 10 }}>Quick Fill from Clients</div>
              {[
                { name: "Sharma Trading Co.", pan: "AABCS1429B", gstin: "27AABCS1429B1ZS" },
                { name: "Kapoor Pharma Pvt Ltd", pan: "AAECK5421M", gstin: "27AAECK5421M1ZT" },
                { name: "Mehta Exports LLP", pan: "BBBFM8272G", gstin: "27BBBFM8272G1ZP" },
              ].map((c, i) => (
                <button key={i} onClick={() => { setClientName(c.name); setPan(c.pan); setGstin(c.gstin); }}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    width: "100%", padding: "7px 10px",
                    background: t.sf2, border: `1px solid ${t.bd}`,
                    borderRadius: 7, cursor: "pointer", marginBottom: 6,
                    textAlign: "left", transition: "all .15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = t.p; e.currentTarget.style.background = t.sfH; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = t.bd; e.currentTarget.style.background = t.sf2; }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: t.tx }}>{c.name}</div>
                    <div style={{ fontFamily: "monospace", fontSize: 9, color: t.txD }}>{c.pan}</div>
                  </div>
                  <span style={{ color: t.p, fontSize: 14 }}>→</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
